import React, { useState, useEffect } from 'react';
import { MapContainer, TileLayer, Marker, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import { useApp } from '@/context/AppContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { AlertTriangle, MapPin, Clock, Plus, ThumbsUp, ThumbsDown } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { toast } from 'sonner';
import { reportsAPI } from '@/lib/api';
import 'leaflet/dist/leaflet.css';

const HAZARD_SUBTYPES = [
  { value: 'debris', label: 'Debris' },
  { value: 'shallow', label: 'Shallow Water' },
  { value: 'rocks', label: 'Rocks' },
  { value: 'buoy_missing', label: 'Missing Buoy' },
  { value: 'strong_current', label: 'Strong Current' },
  { value: 'other_hazard', label: 'Other Hazard' },
];

const ACTIVITY_SUBTYPES = [
  { value: 'patrol', label: 'Coast Guard / Patrol' },
  { value: 'safety_check', label: 'Safety Check Zone' },
  { value: 'regatta', label: 'Regatta / Race' },
  { value: 'fishing_area', label: 'Fishing Area' },
  { value: 'whale_sighting', label: 'Whale / Dolphin' },
  { value: 'other_activity', label: 'Other Activity' },
];

// Location picker component
const LocationPicker = ({ position, setPosition }) => {
  useMapEvents({
    click(e) {
      setPosition([e.latlng.lat, e.latlng.lng]);
    },
  });

  const icon = L.divIcon({
    className: 'custom-pin',
    html: `<div style="
      width: 30px;
      height: 30px;
      background: #ef4444;
      border: 3px solid white;
      border-radius: 50% 50% 50% 0;
      transform: rotate(-45deg);
      box-shadow: 0 2px 8px rgba(0,0,0,0.3);
    "></div>`,
    iconSize: [30, 30],
    iconAnchor: [15, 30],
  });

  return position ? <Marker position={position} icon={icon} /> : null;
};

const ReportsScreen = ({ initialPinPosition, onPinUsed }) => {
  const { myLocation, nearbyReports, createReport, voteOnReport, fetchMapData } = useApp();

  const [activeTab, setActiveTab] = useState(initialPinPosition ? 'create' : 'nearby');
  const [reportType, setReportType] = useState('HAZARD');
  const [subtype, setSubtype] = useState('');
  const [notes, setNotes] = useState('');
  const [pinPosition, setPinPosition] = useState(initialPinPosition || null);
  const [isLoading, setIsLoading] = useState(false);
  const [myReports, setMyReports] = useState([]);

  // Set initial pin position from map
  useEffect(() => {
    if (initialPinPosition) {
      setPinPosition(initialPinPosition);
      setActiveTab('create');
      if (onPinUsed) onPinUsed();
    }
  }, [initialPinPosition, onPinUsed]);

  useEffect(() => {
    fetchMyReports();
  }, []);

  const fetchMyReports = async () => {
    try {
      const response = await reportsAPI.getMine();
      setMyReports(response.data || []);
    } catch (error) {
      console.error('Failed to fetch my reports:', error);
    }
  };

  const handleCreateReport = async () => {
    if (!pinPosition && !myLocation) {
      toast.error('Please select a location on the map');
      return;
    }
    if (!subtype) {
      toast.error('Please select a type');
      return;
    }

    setIsLoading(true);
    try {
      const lat = pinPosition ? pinPosition[0] : myLocation.lat;
      const lon = pinPosition ? pinPosition[1] : myLocation.lon;
      await createReport(reportType, subtype, lat, lon, notes || null);
      setSubtype('');
      setNotes('');
      setPinPosition(null);
      fetchMyReports();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create report');
    } finally {
      setIsLoading(false);
    }
  };

  const handleVote = async (reportId, vote) => {
    try {
      await voteOnReport(reportId, vote);
      fetchMapData();
    } catch (error) {
      toast.error('Failed to vote');
    }
  };

  const subtypes = reportType === 'HAZARD' ? HAZARD_SUBTYPES : ACTIVITY_SUBTYPES;
  const mapCenter = myLocation ? [myLocation.lat, myLocation.lon] : [25.7617, -80.1918];

  return (
    <div className="h-full flex flex-col bg-gray-50" data-testid="reports-screen">
      {/* Header */}
      <div className="bg-white border-b px-4 py-4">
        <h1 className="text-xl font-bold text-gray-800">Reports</h1>
        <p className="text-sm text-gray-500">Report hazards and activities</p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col">
        <TabsList className="mx-4 mt-4 grid w-auto grid-cols-2">
          <TabsTrigger value="nearby">Nearby</TabsTrigger>
          <TabsTrigger value="create">Create Report</TabsTrigger>
        </TabsList>

        {/* Nearby Reports */}
        <TabsContent value="nearby" className="flex-1 mt-0">
          <ScrollArea className="flex-1 px-4 py-4">
            {nearbyReports.length === 0 ? (
              <Card className="border-dashed">
                <CardContent className="p-8 text-center">
                  <AlertTriangle className="w-12 h-12 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-500">No reports nearby</p>
                  <p className="text-sm text-gray-400">Reports within 20nm will appear here</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-3">
                {nearbyReports.map((report) => (
                  <Card key={report.id} data-testid={`report-${report.id}`}>
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between">
                        <div className="flex items-start gap-3">
                          <div
                            className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                              report.type === 'HAZARD' ? 'bg-red-100' : 'bg-amber-100'
                            }`}
                          >
                            {report.type === 'HAZARD' ? (
                              <AlertTriangle className="w-5 h-5 text-red-600" />
                            ) : (
                              <MapPin className="w-5 h-5 text-amber-600" />
                            )}
                          </div>
                          <div>
                            <p className="font-medium text-gray-800 capitalize">
                              {report.subtype.replace(/_/g, ' ')}
                            </p>
                            <Badge
                              variant="outline"
                              className={`text-xs ${
                                report.type === 'HAZARD'
                                  ? 'border-red-200 text-red-600'
                                  : 'border-amber-200 text-amber-600'
                              }`}
                            >
                              {report.type === 'HAZARD' ? 'Hazard' : 'Activity'}
                            </Badge>
                            {report.notes && (
                              <p className="text-sm text-gray-500 mt-1">&quot;{report.notes}&quot;</p>
                            )}
                            <div className="flex items-center gap-2 mt-2">
                              <span className="text-xs text-gray-500">Confidence:</span>
                              <div className="flex">
                                {[1, 2, 3].map((i) => (
                                  <div
                                    key={i}
                                    className={`w-2 h-2 rounded-full mx-0.5 ${
                                      i <= report.confidence ? 'bg-green-500' : 'bg-gray-200'
                                    }`}
                                  />
                                ))}
                              </div>
                            </div>
                            <p className="text-xs text-gray-400 mt-1">
                              <Clock className="w-3 h-3 inline mr-1" />
                              {formatDistanceToNow(new Date(report.created_at))} ago
                            </p>
                          </div>
                        </div>
                        <div className="flex flex-col gap-1">
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-8 text-green-600 hover:bg-green-50"
                            onClick={() => handleVote(report.id, 'CONFIRM')}
                          >
                            <ThumbsUp className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-8 text-red-600 hover:bg-red-50"
                            onClick={() => handleVote(report.id, 'DISMISS')}
                          >
                            <ThumbsDown className="w-4 h-4" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}

            {/* My Reports */}
            {myReports.length > 0 && (
              <div className="mt-6">
                <h2 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-3">
                  My Reports
                </h2>
                <div className="space-y-3">
                  {myReports.slice(0, 5).map((report) => (
                    <Card key={report.id} className="bg-gray-50">
                      <CardContent className="p-3">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <Badge
                              className={`text-xs ${
                                report.type === 'HAZARD' ? 'bg-red-500' : 'bg-amber-500'
                              }`}
                            >
                              {report.subtype.replace(/_/g, ' ')}
                            </Badge>
                            <span className="text-xs text-gray-500">
                              {formatDistanceToNow(new Date(report.created_at))} ago
                            </span>
                          </div>
                          <Badge variant="secondary" className="text-xs">
                            Conf: {report.confidence}
                          </Badge>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>
            )}
          </ScrollArea>
        </TabsContent>

        {/* Create Report */}
        <TabsContent value="create" className="flex-1 mt-0 flex flex-col">
          <div className="flex-1 px-4 py-4 space-y-4">
            {/* Mini Map */}
            <Card>
              <CardHeader className="py-3">
                <CardTitle className="text-sm">Tap to place pin</CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <div className="h-48 rounded-b-lg overflow-hidden">
                  <MapContainer center={mapCenter} zoom={14} className="h-full w-full">
                    {/* ESRI Ocean base layer */}
                    <TileLayer 
                      url="https://server.arcgisonline.com/ArcGIS/rest/services/Ocean/World_Ocean_Base/MapServer/tile/{z}/{y}/{x}"
                      maxZoom={13}
                    />
                    {/* OpenSeaMap nautical overlay */}
                    <TileLayer 
                      url="https://tiles.openseamap.org/seamark/{z}/{x}/{y}.png"
                      maxZoom={18}
                    />
                    <LocationPicker position={pinPosition} setPosition={setPinPosition} />
                  </MapContainer>
                </div>
              </CardContent>
            </Card>

            {/* Report Form */}
            <Card>
              <CardContent className="p-4 space-y-4">
                {/* Type Selection */}
                <div className="flex gap-2">
                  <Button
                    type="button"
                    variant={reportType === 'HAZARD' ? 'default' : 'outline'}
                    className={reportType === 'HAZARD' ? 'bg-red-500 hover:bg-red-600' : ''}
                    onClick={() => {
                      setReportType('HAZARD');
                      setSubtype('');
                    }}
                    data-testid="report-type-hazard"
                  >
                    <AlertTriangle className="w-4 h-4 mr-1" /> Hazard
                  </Button>
                  <Button
                    type="button"
                    variant={reportType === 'MARINE_ACTIVITY' ? 'default' : 'outline'}
                    className={
                      reportType === 'MARINE_ACTIVITY' ? 'bg-amber-500 hover:bg-amber-600' : ''
                    }
                    onClick={() => {
                      setReportType('MARINE_ACTIVITY');
                      setSubtype('');
                    }}
                    data-testid="report-type-activity"
                  >
                    <MapPin className="w-4 h-4 mr-1" /> Activity
                  </Button>
                </div>

                {/* Subtype */}
                <div>
                  <Label>Type</Label>
                  <Select value={subtype} onValueChange={setSubtype}>
                    <SelectTrigger className="mt-2" data-testid="report-subtype-select">
                      <SelectValue placeholder="Select type..." />
                    </SelectTrigger>
                    <SelectContent>
                      {subtypes.map((st) => (
                        <SelectItem key={st.value} value={st.value}>
                          {st.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Notes */}
                <div>
                  <Label htmlFor="notes">Notes (optional)</Label>
                  <Input
                    id="notes"
                    placeholder="Add details..."
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    className="mt-2"
                    data-testid="report-notes-input"
                  />
                </div>

                {/* Submit */}
                <Button
                  onClick={handleCreateReport}
                  disabled={isLoading || !subtype}
                  className="w-full bg-cyan-500 hover:bg-cyan-600"
                  data-testid="submit-report-btn"
                >
                  <Plus className="w-4 h-4 mr-1" />
                  {isLoading ? 'Creating...' : 'Create Report'}
                </Button>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default ReportsScreen;
